// ============================================================
// AiVatBatchDialog.tsx
// ------------------------------------------------------------
// FAS B (2026-05-21): AI-baserad batch-extraktion av moms-data
// för poster som saknar vatAmount/vatRate.
//
// Flöde:
//   1) Modal listar alla poster utan moms-data (per kategori)
//   2) Användaren laddar upp PDF-filer (multi-select) — matchas
//      mot poster via filename
//   3) Per matchad fil: anropa aiExtract() -> uppdatera posten
//      i Firestore med vatAmount/vatRate + vatStatus='ai_extracted'
//   4) Progress + success/failure per post
//   5) Rate limit: max 5 samtidiga, retry på 429
//
// SVENSKA UI-strängar. Använder samma modal-stil som
// JsonImportDialog/DuplicateComparePopup.
// ============================================================

import { useEffect, useMemo, useRef, useState } from 'react'
import { doc, updateDoc } from 'firebase/firestore'
import { db, auth } from '../firebase'
import { aiExtract } from '../lib/aiExtract'
import { uploadAndAttachStoragePath, type CloudReceipt } from '../lib/cloudStore'

const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

interface Props {
  open: boolean
  onClose: () => void
  companyId: string
  companyName: string
  receipts: CloudReceipt[]   // poster som saknar moms-data
}

type Phase = 'idle' | 'files-loaded' | 'running' | 'done' | 'error'

interface PostResult {
  receiptId: string
  filename: string
  status: 'pending' | 'matched' | 'no-file' | 'success' | 'failed'
  error?: string
  vatRate?: number
  vatAmount?: number
  /** Normaliserat filnamn — för matchning-diagnostik */
  normalizedName?: string
}

const CAT_LABEL: Record<CloudReceipt['category'], string> = {
  kundfakturor: 'Kundfakturor',
  leverantorsfakturor: 'Leverantörsfakturor',
  utlagg: 'Utlägg',
}

// 2026-05-24: raised from 5 → 10 per request. Anthropic Haiku tier-1 RPM
// handles ~50 RPM; with up-to-3 retries on 429 that's worst case 30 calls/min
// from a single bulk run, comfortably under the limit.
const MAX_CONCURRENT = 10
const MAX_RETRIES = 3

/** Sleep with jitter for backoff */
function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

/** Normalisera filnamn för matchning — strip path, lowercase, ta bort spaces */
function normFilename(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? name
  return base.toLowerCase().replace(/\s+/g, '').trim()
}

export function AiVatBatchDialog({ open, onClose, companyId, companyName, receipts }: Props) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [files, setFiles] = useState<Map<string, File>>(new Map())
  const [results, setResults] = useState<PostResult[]>([])
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const cancelRef = useRef<boolean>(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Kollapsbara sektioner i diagnostik
  const [openSec, setOpenSec] = useState<{
    success: boolean; failed: boolean; noFile: boolean; orphan: boolean; matching: boolean
  }>({ success: false, failed: true, noFile: true, orphan: false, matching: false })
  // Selektion för retry
  const [retrySel, setRetrySel] = useState<Set<string>>(new Set())

  // Reset state när dialogen stängs/öppnas
  useEffect(() => {
    if (!open) {
      setPhase('idle')
      setFiles(new Map())
      setResults([])
      setProgress({ done: 0, total: 0 })
      setErrorMsg(null)
      setRetrySel(new Set())
      cancelRef.current = false
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [open])

  // Escape för att stänga (om inte körning pågår)
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase !== 'running') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose, phase])

  // Gruppera poster per kategori (för översikt)
  const byCategory = useMemo(() => {
    const m = new Map<CloudReceipt['category'], CloudReceipt[]>()
    for (const r of receipts) {
      const arr = m.get(r.category) ?? []
      arr.push(r)
      m.set(r.category, arr)
    }
    return m
  }, [receipts])

  const totalAmount = useMemo(
    () => receipts.reduce((s, r) => s + (r.amount ?? 0), 0),
    [receipts]
  )

  function handleFilesSelected(selected: FileList | null) {
    if (!selected || selected.length === 0) return
    const map = new Map<string, File>(files)
    for (let i = 0; i < selected.length; i++) {
      const f = selected[i]
      map.set(normFilename(f.name), f)
    }
    setFiles(map)
    setPhase('files-loaded')
  }

  /** Beräkna initialt match-state per post baserat på laddade filer */
  function buildInitialResults(): PostResult[] {
    return receipts.map((r) => {
      const key = normFilename(r.filename)
      const f = files.get(key)
      return {
        receiptId: r.id,
        filename: r.filename,
        status: f ? 'matched' : 'no-file',
        normalizedName: key,
      }
    })
  }

  /** Retry-knapp: kör om AI-analys på markerade misslyckade poster */
  async function retryFailed(failedIds: string[]) {
    if (failedIds.length === 0) return
    const idsSet = new Set(failedIds)
    const targets: Array<{ receipt: CloudReceipt; file: File; idx: number }> = []
    receipts.forEach((r, idx) => {
      if (!idsSet.has(r.id)) return
      const f = files.get(normFilename(r.filename))
      if (f) targets.push({ receipt: r, file: f, idx })
    })

    if (targets.length === 0) {
      setErrorMsg('Kan inte göra om — filerna är inte längre uppladdade')
      return
    }

    cancelRef.current = false
    setPhase('running')
    setProgress({ done: 0, total: targets.length })

    const updated = [...results]
    // Markera retry-targets som "matched" igen i lokal state
    targets.forEach((t) => {
      updated[t.idx] = { ...updated[t.idx], status: 'matched', error: undefined }
    })
    setResults([...updated])

    let done = 0
    for (let i = 0; i < targets.length; i += MAX_CONCURRENT) {
      if (cancelRef.current) break
      const chunk = targets.slice(i, i + MAX_CONCURRENT)
      const settled = await Promise.all(
        chunk.map((t) => processOne(t.receipt, t.file).then((res) => ({ res, idx: t.idx })))
      )
      for (const { res, idx } of settled) {
        updated[idx] = { ...res, normalizedName: updated[idx].normalizedName }
      }
      done += chunk.length
      setProgress({ done, total: targets.length })
      setResults([...updated])
    }
    setPhase('done')
  }

  /** Anropa AI på en post — med retry på 429 */
  async function processOne(receiptArg: CloudReceipt, file: File): Promise<PostResult> {
    let r: CloudReceipt = receiptArg
    const uid = auth.currentUser?.uid
    if (!uid) {
      return { receiptId: r.id, filename: r.filename, status: 'failed', error: 'Inte inloggad' }
    }

    // 2026-05-21 (Re-run AI feature): innan AI-anropet, ladda upp PDF till
    // Firebase Storage om posten saknar storagePath. Pa sa satt kraver
    // framtida korningar (via "Kor AI igen"-knappen) ingen re-upload.
    // Om uppladdningen misslyckas fortsatter vi anda med AI pa local file.
    if (!r.storagePath) {
      try {
        const { storagePath, downloadUrl } = await uploadAndAttachStoragePath(companyId, r, file)
        r = { ...r, storagePath, downloadUrl }
      } catch (uploadErr) {
        console.warn('[AiVatBatch] PDF-uppladdning misslyckades — fortsatter med AI:', (uploadErr as Error).message)
      }
    }

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      if (cancelRef.current) {
        return { receiptId: r.id, filename: r.filename, status: 'failed', error: 'Avbruten' }
      }
      try {
        const res = await aiExtract(file)
        if (res.error) {
          // Retry vid 429-liknande fel
          if (/429|rate|too many/i.test(res.error) && attempt < MAX_RETRIES) {
            await sleep(1000 * Math.pow(2, attempt - 1) + Math.random() * 300)
            continue
          }
          return { receiptId: r.id, filename: r.filename, status: 'failed', error: res.error }
        }

        // Uppdatera Firestore-posten med moms-data
        const patch: Record<string, unknown> = {}
        if (res.vatAmount != null) patch.vatAmount = res.vatAmount
        if (res.vatRate != null) patch.vatRate = res.vatRate
        if (res.amountExVat != null) patch.amountExVat = res.amountExVat
        if (res.currency && !r.currency) patch.currency = res.currency

        if (patch.vatAmount == null && patch.vatRate == null) {
          return {
            receiptId: r.id, filename: r.filename, status: 'failed',
            error: 'AI hittade ingen moms-data i kvittot',
          }
        }
        patch.vatStatus = 'ai_extracted'

        const ref = doc(db, 'users', uid, 'companies', companyId, 'receipts', r.id)
        await updateDoc(ref, patch)

        return {
          receiptId: r.id,
          filename: r.filename,
          status: 'success',
          vatAmount: res.vatAmount,
          vatRate: res.vatRate,
        }
      } catch (e) {
        if (attempt < MAX_RETRIES) {
          await sleep(1000 * Math.pow(2, attempt - 1) + Math.random() * 300)
          continue
        }
        return {
          receiptId: r.id, filename: r.filename, status: 'failed',
          error: (e as Error).message,
        }
      }
    }
    return { receiptId: r.id, filename: r.filename, status: 'failed', error: 'Max retries' }
  }

  async function runBatch() {
    setErrorMsg(null)
    cancelRef.current = false

    const initialResults = buildInitialResults()
    setResults(initialResults)

    const targets: Array<{ receipt: CloudReceipt; file: File; idx: number }> = []
    receipts.forEach((r, idx) => {
      const f = files.get(normFilename(r.filename))
      if (f) targets.push({ receipt: r, file: f, idx })
    })

    if (targets.length === 0) {
      setErrorMsg('Inga filer matchar några poster — kontrollera filnamnen')
      return
    }

    setPhase('running')
    setProgress({ done: 0, total: targets.length })

    let done = 0
    const updated = [...initialResults]

    // Concurrency limit: process i grupper om MAX_CONCURRENT
    for (let i = 0; i < targets.length; i += MAX_CONCURRENT) {
      if (cancelRef.current) break
      const chunk = targets.slice(i, i + MAX_CONCURRENT)
      const settled = await Promise.all(
        chunk.map((t) => processOne(t.receipt, t.file).then((res) => ({ res, idx: t.idx })))
      )
      for (const { res, idx } of settled) {
        updated[idx] = res
      }
      done += chunk.length
      setProgress({ done, total: targets.length })
      setResults([...updated])
    }

    setPhase('done')
  }

  if (!open) return null

  const successCount = results.filter((r) => r.status === 'success').length
  const failedCount = results.filter((r) => r.status === 'failed').length
  const noFileCount = results.filter((r) => r.status === 'no-file').length

  // Räkna matchade utifrån aktuella filer (state) — används för run-knappens etikett
  let matchedFromFiles = 0
  for (const r of receipts) {
    if (files.has(normFilename(r.filename))) matchedFromFiles++
  }

  return (
    <div
      onClick={() => { if (phase !== 'running') onClose() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '2rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '1.25rem 1.5rem',
          width: 'min(820px, 100%)',
          maxHeight: '90vh',
          overflowY: 'auto',
          color: 'var(--text)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>
            {'🤖 AI-moms-analys — ' + companyName}
          </div>
          <button
            onClick={onClose}
            disabled={phase === 'running'}
            style={{
              background: 'none', border: 'none', color: 'var(--text-muted)',
              fontSize: '1.3rem', cursor: phase === 'running' ? 'not-allowed' : 'pointer',
              padding: '0.25rem', opacity: phase === 'running' ? 0.3 : 1,
            }}
          >{'✕'}</button>
        </div>

        <div style={{ fontSize: '0.88rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
          {receipts.length + ' poster saknar moms-data (' + fmt(totalAmount) + ' kr totalt)'}
        </div>

        {/* Sammanfattning per kategori */}
        <div style={{
          border: '1px solid var(--border)', borderRadius: 8,
          padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
        }}>
          {Array.from(byCategory.entries()).map(([cat, list]) => {
            const sum = list.reduce((s, r) => s + (r.amount ?? 0), 0)
            return (
              <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', fontSize: '0.85rem' }}>
                <span>{CAT_LABEL[cat]}</span>
                <span style={{ color: 'var(--text-muted)' }}>
                  {list.length + ' st · ' + fmt(sum) + ' kr'}
                </span>
              </div>
            )
          })}
        </div>

        {/* Fil-uppladdning */}
        {(phase === 'idle' || phase === 'files-loaded') && (
          <div style={{
            background: 'rgba(59,130,246,0.08)',
            border: '1px solid #3b82f6',
            borderRadius: 6,
            padding: '0.6rem 0.8rem',
            marginBottom: '0.75rem',
            fontSize: '0.85rem',
          }}>
            <div style={{ marginBottom: '0.5rem' }}>
              Ladda upp PDF-filerna från Dropbox-mappen. Filer matchas mot poster
              via <code>filename</code>. xAI Grok körs på varje matchad fil för
              att extrahera moms-data.
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf,image/*"
              multiple
              onChange={(e) => handleFilesSelected(e.target.files)}
            />
            {files.size > 0 && (
              <div style={{ marginTop: '0.5rem', color: 'var(--text-muted)' }}>
                {files.size + ' filer laddade'}
              </div>
            )}
          </div>
        )}

        {/* Match-preview innan run */}
        {phase === 'files-loaded' && files.size > 0 && (
          <div style={{
            background: 'rgba(16,185,129,0.08)',
            border: '1px solid #10b981',
            borderRadius: 6,
            padding: '0.6rem 0.8rem',
            marginBottom: '0.75rem',
            fontSize: '0.85rem',
          }}>
            {(() => {
              const matches = receipts.filter((r) => files.has(normFilename(r.filename))).length
              const noMatch = receipts.length - matches
              return (
                <>
                  <div style={{ fontWeight: 600 }}>
                    {matches + ' av ' + receipts.length + ' poster matchade'}
                  </div>
                  {noMatch > 0 && (
                    <div style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                      {noMatch + ' poster utan PDF-fil — hoppas över'}
                    </div>
                  )}
                </>
              )
            })()}
          </div>
        )}

        {errorMsg && (
          <div style={{
            background: 'rgba(239,68,68,0.10)', border: '1px solid #ef4444',
            borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
            color: '#ef4444', fontSize: '0.85rem',
          }}>
            {'⚠ ' + errorMsg}
          </div>
        )}

        {/* Run + Progress */}
        {phase === 'running' && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.4rem' }}>
              {'Bearbetar ' + progress.done + ' / ' + progress.total + '… (max ' + MAX_CONCURRENT + ' samtidigt)'}
            </div>
            <div style={{
              height: 8, background: 'rgba(255,255,255,0.08)',
              borderRadius: 4, overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: (progress.total > 0 ? (progress.done / progress.total) * 100 : 0) + '%',
                background: '#10b981', transition: 'width 0.2s ease',
              }} />
            </div>
            <div style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              {'Lyckade: ' + successCount + ' · Misslyckade: ' + failedCount}
            </div>
          </div>
        )}

        {/* Detaljerad diagnostik (FAS C 2026-05-21) */}
        {(phase === 'running' || phase === 'done') && results.length > 0 && (() => {
          const successList = results.filter((r) => r.status === 'success')
          const failedList = results.filter((r) => r.status === 'failed')
          const noFileList = results.filter((r) => r.status === 'no-file')

          // Orphan files = uppladdade filer som INTE matchade någon Firestore-post
          const matchedKeys = new Set<string>()
          for (const r of receipts) matchedKeys.add(normFilename(r.filename))
          const orphanFiles: File[] = []
          for (const [key, f] of files.entries()) {
            if (!matchedKeys.has(key)) orphanFiles.push(f)
          }

          return (
            <div style={{ marginBottom: '0.75rem' }}>
              {/* Lyckade */}
              <DiagSection
                title={'✅ Lyckade (' + successList.length + ')'}
                color="#10b981"
                isOpen={openSec.success}
                onToggle={() => setOpenSec((s) => ({ ...s, success: !s.success }))}
              >
                {successList.length === 0 ? (
                  <div style={diagEmptyStyle}>Inga lyckade än</div>
                ) : (
                  successList.map((r) => (
                    <div key={r.receiptId} style={diagRowStyle} title={r.filename}>
                      <span style={diagFilenameStyle}>{r.filename}</span>
                      <span style={{ color: '#10b981', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                        {r.vatRate != null ? (r.vatRate * 100).toFixed(0) + '%' : '?'}
                        {r.vatAmount != null ? ' · ' + fmt(r.vatAmount) + ' kr' : ''}
                      </span>
                    </div>
                  ))
                )}
              </DiagSection>

              {/* Misslyckade — med retry */}
              <DiagSection
                title={'⚠️ Misslyckade (' + failedList.length + ')'}
                color="#ef4444"
                isOpen={openSec.failed}
                onToggle={() => setOpenSec((s) => ({ ...s, failed: !s.failed }))}
                rightActions={failedList.length > 0 && phase !== 'running' ? (
                  <div style={{ display: 'flex', gap: '0.3rem' }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (retrySel.size === failedList.length) {
                          setRetrySel(new Set())
                        } else {
                          setRetrySel(new Set(failedList.map((r) => r.receiptId)))
                        }
                      }}
                      style={smallBtnStyle}
                    >
                      {retrySel.size === failedList.length && failedList.length > 0 ? 'Avmarkera alla' : 'Markera alla'}
                    </button>
                    <button
                      type="button"
                      disabled={retrySel.size === 0}
                      onClick={(e) => {
                        e.stopPropagation()
                        retryFailed(Array.from(retrySel))
                        setRetrySel(new Set())
                      }}
                      style={{
                        ...smallBtnStyle,
                        background: retrySel.size > 0 ? '#f59e0b' : 'rgba(255,255,255,0.06)',
                        color: retrySel.size > 0 ? '#fff' : 'var(--text-muted)',
                        cursor: retrySel.size > 0 ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {'Försök igen (' + retrySel.size + ')'}
                    </button>
                  </div>
                ) : null}
              >
                {failedList.length === 0 ? (
                  <div style={diagEmptyStyle}>Inga fel</div>
                ) : (
                  failedList.map((r) => {
                    const checked = retrySel.has(r.receiptId)
                    return (
                      <div key={r.receiptId} style={diagRowStyle} title={r.filename}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(retrySel)
                            if (e.target.checked) next.add(r.receiptId)
                            else next.delete(r.receiptId)
                            setRetrySel(next)
                          }}
                          style={{ marginRight: '0.4rem', flexShrink: 0 }}
                          disabled={phase === 'running'}
                        />
                        <span style={diagFilenameStyle}>{r.filename}</span>
                        <span style={{ color: '#ef4444', whiteSpace: 'nowrap', fontSize: '0.75rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.error}>
                          {r.error ?? 'fel'}
                        </span>
                      </div>
                    )
                  })
                )}
              </DiagSection>

              {/* Ingen matchande fil */}
              <DiagSection
                title={'❓ Ingen matchande fil (' + noFileList.length + ')'}
                color="#f59e0b"
                isOpen={openSec.noFile}
                onToggle={() => setOpenSec((s) => ({ ...s, noFile: !s.noFile }))}
              >
                {noFileList.length === 0 ? (
                  <div style={diagEmptyStyle}>Alla poster har en uppladdad fil</div>
                ) : (
                  <>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', padding: '0.2rem 0.4rem' }}>
                      Firestore-poster utan matchande fil — antingen filnamn-mismatch eller fil saknas helt:
                    </div>
                    {noFileList.map((r) => (
                      <div key={r.receiptId} style={diagRowStyle} title={r.filename}>
                        <span style={diagFilenameStyle}>{r.filename}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                          norm: {r.normalizedName ?? '?'}
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </DiagSection>

              {/* Orphan filer */}
              <DiagSection
                title={'📄 Uppladdade filer utan matchande post (' + orphanFiles.length + ')'}
                color="#8b5cf6"
                isOpen={openSec.orphan}
                onToggle={() => setOpenSec((s) => ({ ...s, orphan: !s.orphan }))}
              >
                {orphanFiles.length === 0 ? (
                  <div style={diagEmptyStyle}>Alla uppladdade filer matchade en post</div>
                ) : (
                  <>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', padding: '0.2rem 0.4rem' }}>
                      Filer som du laddade upp men som inte matchade någon Firestore-post:
                    </div>
                    {orphanFiles.map((f, i) => (
                      <div key={f.name + i} style={diagRowStyle} title={f.name}>
                        <span style={diagFilenameStyle}>{f.name}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                          norm: {normFilename(f.name)}
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </DiagSection>

              {/* Matchnings-diagnostik */}
              <DiagSection
                title={'🔍 Filnamn-jämförelse'}
                color="#3b82f6"
                isOpen={openSec.matching}
                onToggle={() => setOpenSec((s) => ({ ...s, matching: !s.matching }))}
              >
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', padding: '0.2rem 0.4rem' }}>
                  Båda kolumner normaliseras (lowercase, ta bort blanksteg) före jämförelse.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', padding: '0.3rem 0.4rem', fontSize: '0.72rem' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                      Firestore ({receipts.length})
                    </div>
                    <div style={{ maxHeight: 180, overflowY: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: 4, padding: '0.3rem' }}>
                      {receipts.slice(0, 50).map((r) => {
                        const key = normFilename(r.filename)
                        const matched = files.has(key)
                        return (
                          <div key={r.id} style={{ color: matched ? '#10b981' : '#ef4444', fontSize: '0.7rem', padding: '1px 0' }}>
                            {matched ? '✓' : '✗'} {key}
                          </div>
                        )
                      })}
                      {receipts.length > 50 && <div style={{ color: 'var(--text-muted)' }}>... {receipts.length - 50} fler</div>}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                      Uppladdade filer ({files.size})
                    </div>
                    <div style={{ maxHeight: 180, overflowY: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: 4, padding: '0.3rem' }}>
                      {Array.from(files.entries()).slice(0, 50).map(([key]) => {
                        const matched = matchedKeys.has(key)
                        return (
                          <div key={key} style={{ color: matched ? '#10b981' : '#f59e0b', fontSize: '0.7rem', padding: '1px 0' }}>
                            {matched ? '✓' : '○'} {key}
                          </div>
                        )
                      })}
                      {files.size > 50 && <div style={{ color: 'var(--text-muted)' }}>... {files.size - 50} fler</div>}
                    </div>
                  </div>
                </div>
              </DiagSection>
            </div>
          )
        })()}

        {/* Klar — sammanfattning */}
        {phase === 'done' && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem',
            marginBottom: '0.75rem',
          }}>
            <div style={{ padding: '0.5rem', background: 'rgba(16,185,129,0.1)', border: '1px solid #10b981', borderRadius: 6 }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Lyckade</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 600, color: '#10b981' }}>{successCount}</div>
            </div>
            <div style={{ padding: '0.5rem', background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 6 }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Misslyckade</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 600, color: '#ef4444' }}>{failedCount}</div>
            </div>
            <div style={{ padding: '0.5rem', background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b', borderRadius: 6 }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Ingen fil</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 600, color: '#f59e0b' }}>{noFileCount}</div>
            </div>
          </div>
        )}

        {/* Knappar */}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          {phase === 'running' && (
            <button
              onClick={() => { cancelRef.current = true }}
              style={{
                background: 'rgba(239,68,68,0.15)', color: '#ef4444',
                border: '1px solid #ef4444', borderRadius: 6,
                padding: '0.5rem 1rem', fontSize: '0.88rem', cursor: 'pointer',
              }}
            >Avbryt körning</button>
          )}
          {phase !== 'running' && (
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.06)', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 6,
                padding: '0.5rem 1rem', fontSize: '0.88rem', cursor: 'pointer',
              }}
            >Stäng</button>
          )}
          {phase === 'files-loaded' && files.size > 0 && matchedFromFiles > 0 && (
            <button
              onClick={runBatch}
              style={{
                background: '#10b981', color: '#fff', border: 'none',
                borderRadius: 6, padding: '0.5rem 1rem',
                fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer',
              }}
            >{'Kör AI-analys på ' + matchedFromFiles + ' poster'}</button>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Diagnostik-helpers ---

function DiagSection({
  title, color, isOpen, onToggle, rightActions, children,
}: {
  title: string
  color: string
  isOpen: boolean
  onToggle: () => void
  rightActions?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div style={{
      border: '1px solid var(--border)',
      borderLeft: '3px solid ' + color,
      borderRadius: 6,
      marginBottom: '0.4rem',
      overflow: 'hidden',
    }}>
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.4rem 0.7rem', cursor: 'pointer',
          background: 'rgba(0,0,0,0.18)',
          fontSize: '0.83rem', fontWeight: 600,
          userSelect: 'none',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span style={{ display: 'inline-block', width: 10, color: 'var(--text-muted)' }}>
            {isOpen ? '▼' : '▶'}
          </span>
          <span style={{ color }}>{title}</span>
        </span>
        {rightActions && <span onClick={(e) => e.stopPropagation()}>{rightActions}</span>}
      </div>
      {isOpen && (
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {children}
        </div>
      )}
    </div>
  )
}

const diagRowStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '0.25rem 0.6rem',
  borderTop: '1px solid var(--border)',
  fontSize: '0.78rem',
  gap: '0.5rem',
}

const diagFilenameStyle: React.CSSProperties = {
  flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}

const diagEmptyStyle: React.CSSProperties = {
  padding: '0.4rem 0.7rem',
  fontSize: '0.78rem',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
}

const smallBtnStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '2px 8px',
  fontSize: '0.72rem',
  cursor: 'pointer',
}

export default AiVatBatchDialog
